import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, NgZone, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Style, StatusBar } from '@capacitor/status-bar';
import { CookieConsentService, SafariViewWrapperService } from '@cnt-workspace/data-access';
import {
  BottomNavComponent,
  BottomNavVisibilityService,
  CookieConsentComponent,
  ToastHostComponent,
  prefersReducedMotion,
} from '@cnt-workspace/ui';
import { gsap } from 'gsap';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { environment } from '../environments/environment';

declare global {
  interface Window {
    dataLayer?: unknown[];
    dataLayer2?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

@Component({
  standalone: true,
  imports: [RouterModule, ToastHostComponent, BottomNavComponent, CookieConsentComponent],
  selector: 'cnt-workspace-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private zone = inject(NgZone);
  private cookieConsent = inject(CookieConsentService);
  private safariView = inject(SafariViewWrapperService);
  /** Read by the template: the shell only reserves room for the phone bottom nav when it shows. */
  readonly bottomNav = inject(BottomNavVisibilityService);
  /** Read by the template: a cookie-consent banner has nothing to do inside the native app shell. */
  protected readonly isNative = isPlatformBrowser(this.platformId) && Capacitor.isNativePlatform();

  title = 'cnt-workspace';

  private cursorCleanup: (() => void) | null = null;
  private routerSub: Subscription | null = null;
  private consentSub: Subscription | null = null;
  private analyticsRouterSub: Subscription | null = null;
  private analyticsLoaded = false;
  private appUrlOpenListener: PluginListenerHandle | null = null;

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initCustomCursor();
      this.initPageTransitions();
      this.initAnalytics();
      if (Capacitor.isNativePlatform()) {
        this.initNativeShell();
      }
    }
  }

  /**
   * Mirrors apps/curbnturf-mobile/src/app/app.component.ts's appUrlOpen handling —
   * Cognito's hosted UI redirects back into the app via the `curbnturf://` custom
   * URL scheme (see environment.prod.ts's cognitoCallbackIOS) or, on Android, a
   * verified https://www.curbnturf.com App Link. Both need to land on this app's
   * own router rather than opening a browser tab.
   */
  private initNativeShell(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url = event.url;
        if (url.startsWith('curbnturf://')) {
          // The iOS social-login redirect lands here from the Hosted UI's
          // SFSafariViewController (AuthService.signInWithProvider) — close it,
          // or it stays on top of the app.
          this.safariView.closeAll().catch(() => void 0);
          this.router.navigateByUrl('/' + url.replace('curbnturf://', ''));
        } else if (url.includes('curbnturf.com')) {
          const slug = url.split('curbnturf.com').pop();
          if (slug) this.router.navigateByUrl(slug);
        }
      });
    }).then((handle) => (this.appUrlOpenListener = handle));

    StatusBar.setOverlaysWebView({ overlay: false }).catch(() => void 0);
    StatusBar.setStyle({ style: Style.Light }).catch(() => void 0);
    StatusBar.setBackgroundColor({ color: '#f7f5ec' }).catch(() => void 0);
  }

  private initPageTransitions(): void {
    // WCAG 2.3.3 — this is motion triggered directly by a user action (following
    // a link), app-wide, so it needs an opt-out. Skipping the fromTo entirely is
    // safe: without it the outlet simply renders at its natural opacity.
    if (prefersReducedMotion()) return;

    // Animate the routed page component when the PAGE changes — not when only
    // the query string does.
    //
    // NavigationEnd fires for query-param-only navigations too, and the search
    // page issues a real router.navigate for every filter mutation
    // (search-results.component.ts syncToUrl(); replaceUrl:true suppresses the
    // history entry, not the navigation). So every slider drag, amenity toggle
    // and view switch used to fade the whole page — navbar included, since the
    // navbar lives inside the routed host — from opacity 0. Measured: one filter
    // change drove the host from 0 back to 1 across ~50 frames.
    //
    // That is also why a 0.42s animation was reported as taking 3-6s to settle:
    // each change re-seeded opacity 0 before the previous tween landed, so the
    // fades stacked. Comparing the path fixes the cause; killing the in-flight
    // tween stops any remaining overlap from compounding.
    let lastPath: string | null = null;
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const path = e.urlAfterRedirects.split('?')[0].split('#')[0];
        if (path === lastPath) return;
        lastPath = path;

        // Small delay so the new component is in the DOM
        requestAnimationFrame(() => {
          const outlet = document.querySelector('router-outlet + *') as HTMLElement;
          if (outlet) {
            gsap.killTweensOf(outlet);
            gsap.fromTo(
              outlet,
              { opacity: 0, y: 14 },
              { opacity: 1, y: 0, duration: 0.42, ease: 'power2.out', clearProps: 'transform' },
            );
          }
        });
      });
  }

  private initCustomCursor(): void {
    if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
    let cursor = document.getElementById('cntCursor');
    if (!cursor) return;

    // Deliberately NOT an early return under reduced motion. styles.scss sets
    // `cursor: none !important` globally, so bailing out of this method would
    // leave the user with no visible pointer at all. Collapse the eased lag
    // instead — the cursor then tracks the pointer exactly, with no motion of
    // its own.
    const followDuration = prefersReducedMotion() ? 0 : 0.15;
    const makeQuickTo = (el: HTMLElement, prop: 'x' | 'y') =>
      gsap.quickTo(el, prop, { duration: followDuration, ease: 'power3.out' });
    let xTo = makeQuickTo(cursor, 'x');
    let yTo = makeQuickTo(cursor, 'y');

    type BaseState = 'default' | 'link' | 'text';
    let baseState: BaseState = 'default';
    let isDown = false;
    // Hotspot offset per state, so each SVG's tip lands on the pointer. Kept in
    // JS rather than CSS custom properties: reading those meant a
    // getComputedStyle() — a forced style recalc — on every mousemove.
    const TIP: Record<string, [number, number]> = {
      default: [-4, -2],
      link: [-4, -2],
      text: [-16, -16],
      grab: [-16, -16],
    };
    let state = 'default';
    const setState = (s: string) => {
      state = s;
      cursor?.setAttribute('data-state', s);
    };

    // quickTo only moves the cursor when GSAP's ticker fires on rAF. After the
    // tab sits in the background (tab freezing, OS sleep, bfcache restore) the
    // pending rAF can be dropped while GSAP still believes its ticker is
    // active, so resetTo() never re-arms it — every tween freezes and the
    // cursor stays stuck where it was, with the OS cursor hidden. Detect that
    // (mouse moving for STALL_MS without the frame counter advancing), kick the
    // ticker, and position the cursor synchronously until frames resume.
    const STALL_MS = 250;
    let stallFrame = -1;
    let stallSince = 0;
    let stalled = false;

    // Fail open: the OS cursor is only hidden (styles.scss) while <html> has
    // ARMED_CLASS, and only a successfully positioned move adds it. A reload
    // where this never runs, a page return, or a detached #cntCursor all fall
    // back to the native cursor instead of leaving no pointer at all.
    const ARMED_CLASS = 'cnt-custom-cursor';
    const root = document.documentElement;
    let armed = false;
    const arm = () => {
      if (armed) return;
      armed = true;
      root.classList.add(ARMED_CLASS);
    };
    const disarm = () => {
      armed = false;
      root.classList.remove(ARMED_CLASS);
    };
    const watchdog = this.zone.runOutsideAngular(() =>
      window.setInterval(() => {
        if (armed && !cursor?.isConnected) disarm();
      }, 1000),
    );

    const onMove = (e: MouseEvent) => {
      if (!cursor?.isConnected) {
        cursor = document.getElementById('cntCursor');
        if (!cursor) return;
        xTo = makeQuickTo(cursor, 'x');
        yTo = makeQuickTo(cursor, 'y');
        setState(baseState);
      }
      if (document.body.classList.contains('cursor-hidden')) {
        document.body.classList.remove('cursor-hidden');
      }

      const [tipX, tipY] = TIP[state] ?? TIP['default'];
      const x = e.clientX + tipX;
      const y = e.clientY + tipY;

      const now = performance.now();
      const frame = gsap.ticker.frame;
      if (frame !== stallFrame) {
        stallFrame = frame;
        stallSince = now;
        stalled = false;
      } else if (!stalled && now - stallSince > STALL_MS) {
        stalled = true;
        gsap.ticker.wake();
      }

      if (stalled) {
        gsap.set(cursor, { x, y });
      } else {
        xTo(x);
        yTo(y);
      }
      arm();
    };
    const onDown = () => {
      isDown = true;
      setState('grab');
    };
    const onUp = () => {
      isDown = false;
      setState(baseState);
    };

    const TEXT_SEL =
      'input:not([type=button]):not([type=submit]):not([type=checkbox]):not([type=radio]), textarea, [contenteditable=true], [contenteditable=""]';
    const LINK_SEL =
      'a, button, [role=button], [class*="cursor-pointer"], .navigation-link, .cnt-nav-link, .magnetic-btn, .btn-3d-wrap, summary, label[for]';

    const onOver = (e: MouseEvent) => {
      if (isDown) return;
      const t = e.target as HTMLElement | null;
      if (!t || !t.closest) return;
      if (t.closest(TEXT_SEL)) baseState = 'text';
      else if (t.closest(LINK_SEL)) baseState = 'link';
      else baseState = 'default';
      setState(baseState);
    };

    const onLeaveDoc = () => document.body.classList.add('cursor-hidden');
    const onEnterDoc = () => document.body.classList.remove('cursor-hidden');
    // Calling ticker.wake() directly bypasses GSAP's "already active" guard: it
    // cancels any stale rAF request and schedules a fresh one. Harmless when
    // the ticker is healthy.
    // Leaving the page disarms, so a return always starts on the native cursor
    // until a move proves the custom one is tracking again.
    const onPageReturn = () => {
      if (document.visibilityState !== 'visible') {
        disarm();
        return;
      }
      gsap.ticker.wake();
      document.body.classList.remove('cursor-hidden');
    };

    // Outside the zone: none of these handlers touch template state, and inside
    // it every mousemove anywhere — plus every GSAP rAF tick the moves schedule —
    // ran change detection on the whole app, starving the cursor of frames on
    // heavy pages.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mousedown', onDown);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('mouseover', onOver);
      document.addEventListener('mouseleave', onLeaveDoc);
      document.addEventListener('mouseenter', onEnterDoc);
      document.addEventListener('visibilitychange', onPageReturn);
      window.addEventListener('pageshow', onPageReturn);
      window.addEventListener('focus', onPageReturn);
      window.addEventListener('pagehide', disarm);
    });

    this.cursorCleanup = () => {
      window.clearInterval(watchdog);
      window.removeEventListener('pagehide', disarm);
      disarm();
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseleave', onLeaveDoc);
      document.removeEventListener('mouseenter', onEnterDoc);
      document.removeEventListener('visibilitychange', onPageReturn);
      window.removeEventListener('pageshow', onPageReturn);
      window.removeEventListener('focus', onPageReturn);
      document.body.classList.remove('cursor-hidden');
    };
  }

  private initAnalytics(): void {
    if (!environment.production) return;

    this.consentSub = this.cookieConsent.accepted$
      .pipe(filter((accepted) => accepted))
      .subscribe(() => this.loadTrackingScripts());
  }

  private loadTrackingScripts(): void {
    if (this.analyticsLoaded) return;
    this.analyticsLoaded = true;

    // Google Ads + GA4 share one dataLayer, matching apps/curbnturf/src/prod/index.html
    window.dataLayer2 = window.dataLayer2 || [];
    const adsScript = document.createElement('script');
    adsScript.async = true;
    adsScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAdsId}`;
    document.head.appendChild(adsScript);

    const ga4Script = document.createElement('script');
    ga4Script.async = true;
    ga4Script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.ga4Code}`;
    document.head.appendChild(ga4Script);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(...(window.dataLayer2 ?? []));
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', environment.ga4Code);

    // Hotjar
    const hotjarInit = document.createElement('script');
    hotjarInit.text = `
      (function (h, o, t, j, a, r) {
        h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); };
        h._hjSettings = { hjid: ${environment.hotjarId}, hjsv: ${environment.hotjarVersion} };
        a = o.getElementsByTagName('head')[0];
        r = o.createElement('script');
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
      })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
    `;
    document.head.appendChild(hotjarInit);

    // Meta Pixel
    const pixelInit = document.createElement('script');
    pixelInit.text = `
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${environment.metaPixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(pixelInit);

    // Fire a GA4 page_view config on every SPA route change, matching curbnturf's app.component.ts
    this.analyticsRouterSub = this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      window.gtag?.('config', environment.ga4Code);
    });
  }

  ngOnDestroy(): void {
    if (this.cursorCleanup) {
      this.cursorCleanup();
    }
    this.routerSub?.unsubscribe();
    this.consentSub?.unsubscribe();
    this.analyticsRouterSub?.unsubscribe();
    this.appUrlOpenListener?.remove();
  }
}
